import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ThemeProvider } from "@mui/material/styles";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { useMainTheme } from "@fuse/core/FuseSettings/hooks/fuseThemeHooks";
import { useSnackbar } from "notistack";
import type { FormSelectOption } from "@/components/ui/forms/formSelectComponent/formSelect.types";
import { useCompany } from "@/contexts/CompanyContext";
import { FormSelect } from "@/components/ui/forms/formSelectComponent/formSelect.component";
import useHeaderTabs from "@/hooks/useHeaderTabs";
import AppDialogConfirmation from "@/components/avulsos/appDialogConfirmation/AppDialogConfirmation";

type FormType = { companyId: string };

export default function SelectCompany() {
	const { companyId, companies, setCompanyId } = useCompany();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [pendingCompanyId, setPendingCompanyId] = useState<string | null>(null);
	const { enqueueSnackbar } = useSnackbar();
	const { clearAllTabs } = useHeaderTabs();

	// Reaplica o tema principal: o dialog e renderizado dentro do ToolbarTheme (paleta escura/texto branco),
	// que atravessa o portal do Dialog. Sem isto, o texto do AppDialogConfirmation fica ilegivel.
	const temaPrincipal = useMainTheme();

	const options: Array<FormSelectOption<string>> = useMemo(
		() =>
			companies
				.slice()
				.sort((a, b) => a.id_cad_empresa - b.id_cad_empresa)
				.map((company) => ({
					value: String(company.id_cad_empresa),
					label: `${company.id_cad_empresa} - ${company.nome_empresa}`,
				})),
		[companies],
	);

	const { control, setValue } = useForm<FormType>({
		defaultValues: { companyId: companyId ?? "" },
	});

	// sincroniza quando o contexto mudar (login/logout)
	useEffect(() => {
		setValue("companyId", companyId ?? "");
	}, [companyId, setValue]);

	if (!companies.length) return null;

	return (
		<div className="w-[30vw] min-w-37.5 max-w-55 min-[768px]:w-75 lg:w-90">
			<FormSelect<FormType, string>
				control={control}
				name="companyId"
				size="small"
				label="Empresa"
				options={options}
				onValueChange={(nextCompanyId) => {
					if (!nextCompanyId || nextCompanyId === companyId) return;

					// Mantém o select apontando para a empresa atual até o usuário confirmar.
					setValue("companyId", companyId ?? "");

					setPendingCompanyId(nextCompanyId);
					setConfirmOpen(true);
				}}
				leftIcon={<FuseSvgIcon size={16}>lucide:building-2</FuseSvgIcon>}
				description=""
				highlightActiveSelection={false}
				multiple={false}
				className="
					[&_.MuiInputLabel-root]:text-[0.6875rem]
					[&_.MuiInputLabel-root]:text-white/70
					[&_.MuiInputLabel-root.Mui-focused]:text-white/90
					[&_.MuiOutlinedInput-root]:rounded-[10px]
					[&_.MuiOutlinedInput-root]:bg-white/10
					[&_.MuiOutlinedInput-root]:text-white/95
					[&_.MuiOutlinedInput-root_.MuiOutlinedInput-notchedOutline]:border-white/30
					[&_.MuiOutlinedInput-root:hover_.MuiOutlinedInput-notchedOutline]:border-white/45
					[&_.MuiOutlinedInput-root.Mui-focused_.MuiOutlinedInput-notchedOutline]:border-white/70
					[&_.MuiInputBase-input]:font-semibold
					[&_.MuiInputBase-input]:text-white/95
					[&_.MuiSelect-select]:min-w-0
					[&_.MuiSelect-select]:overflow-hidden
					[&_.MuiSelect-select]:text-ellipsis
					[&_.MuiSelect-select]:whitespace-nowrap
					[&_.MuiInputAdornment-root]:text-white/80
					[&_.MuiSelect-icon]:text-white/80
					[&_.MuiFormHelperText-root]:hidden
				"
				slotProps={{
					select: {
						MenuProps: {
							PaperProps: {
								className:
									"mt-1.5 rounded-[10px] border border-white/20 bg-slate-900 text-slate-100 shadow-[0_8px_24px_rgba(0,0,0,0.35)] [&_.MuiMenuItem-root]:min-h-[34px] [&_.MuiMenuItem-root]:text-[0.8125rem] [&_.MuiMenuItem-root]:text-inherit [&_.MuiMenuItem-root:hover]:bg-white/10 [&_.MuiMenuItem-root.Mui-selected]:bg-white/20 [&_.MuiMenuItem-root.Mui-selected:hover]:bg-white/30",
							},
						},
					},
				}}
			/>

			<ThemeProvider theme={temaPrincipal}>
				<AppDialogConfirmation
					open={confirmOpen}
					variant="warning"
					title="Atenção"
					message="Você mudou o acesso para outra empresa"
					details={
						<>
							<div className="mb-4">
								Quando você faz a alteração de empresa, os dados a partir de agora que serão lidos e
								gravados serão referentes a essa nova empresa selecionada.
							</div>
							<div className="text-sm font-semibold">
								Todas as abas abertas serão fechadas para evitar inconsistências nos dados exibidos.
							</div>
						</>
					}
					confirmText="Confirmar"
					cancelText="Cancelar"
					onConfirm={() => {
						if (!pendingCompanyId) {
							setConfirmOpen(false);
							return;
						}
						clearAllTabs();
						setCompanyId(pendingCompanyId);
						enqueueSnackbar("Empresa alterada com sucesso.", {
							variant: "success",
						});
						setPendingCompanyId(null);
						setConfirmOpen(false);
					}}
					onCancel={() => {
						setValue("companyId", companyId ?? "");
						setPendingCompanyId(null);
						setConfirmOpen(false);
					}}
				/>
			</ThemeProvider>
		</div>
	);
}
